/**
 * SummaryReport.jsx — v5
 * FIXES:
 *   1 — Removed Surplus & Deficit from screen, Excel and PDF
 *   2 — Narration + Voucher/Receipt No shown in transaction detail rows
 *   3 — Each head prints on a separate page in PDF (page-break-before)
 *   4 — Expense color changed from red → orange (#E8820C)
 */
import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { RefreshCw, FileText, FileSpreadsheet, ChevronDown, ChevronRight } from 'lucide-react';
import { getSummaryReport } from '../../api/yea.api';
import Button from '../../components/common/Button';
import '../accounting/yea.css';

const fmt     = n => Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 });
const fmtDate = d => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '';
const currency = n => Number(n || 0).toFixed(2);

// Fix 4 — orange for expense
const EXPENSE_COLOR         = '#E8820C';
const EXPENSE_COLOR_LIGHT   = '#fff7ed';
const EXPENSE_COLOR_BORDER  = '#fed7aa';
const EXPENSE_COLOR_TEXT    = '#92400e';

// ─── Excel / CSV ──────────────────────────────────────────────────────────────
const downloadExcel = (report, fyName, societyName) => {
  try {
    const R = [];
    const cell = v => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const row  = (...cols) => R.push(cols.map(cell).join(','));

    row(societyName || 'Society');
    row(`Detailed Summary Report — FY: ${fyName || report.fyName}`);
    row(`Generated: ${new Date().toLocaleDateString('en-IN')}`);
    row('');

    // ── INCOME ──
    row('INCOME');
    // Fix 2 — narration + receipt no columns
    row('Sr', 'Head', 'Date', 'Narration', 'Member / Particulars', 'Receipt No', 'Amount (₹)');
    let incSr = 0;
    Object.entries(report.incomeByHeadDetail || {}).forEach(([head, data]) => {
      incSr++;
      row(incSr, head, '', '', '', '', currency(data.total));
      (data.transactions || []).forEach(t => {
        row('', `  ↳ ${head}`, fmtDate(t.date), t.narration || '', t.memberName || '', t.receiptNo || '', currency(t.amount));
      });
    });
    row('', 'TOTAL INCOME', '', '', '', '', currency(report.totalIncome || 0));
    row('');

    // ── EXPENSE ──
    row('EXPENSE');
    // Fix 2 — narration + voucher no columns
    row('Sr', 'Head', 'Date', 'Narration', 'Particulars', 'Voucher No', 'Amount (₹)');
    let expSr = 0;
    Object.entries(report.expenseByHeadDetail || {}).forEach(([head, data]) => {
      expSr++;
      row(expSr, head, '', '', '', '', currency(data.total));
      (data.transactions || []).forEach(t => {
        // Fix 2 — receiptNo field carries voucher no for expense
        row('', `  ↳ ${head}`, fmtDate(t.date), t.narration || '', t.memberName || '', t.receiptNo || '', currency(t.amount));
      });
    });
    row('', 'TOTAL EXPENSE', '', '', '', '', currency(report.totalExpense || 0));
    // Fix 1 — NO surplus/deficit row

    const csv  = R.join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `${(societyName || 'Society').replace(/\s+/g, '_')}_Summary_${(fyName || 'FY').replace(/\//g, '-')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Excel downloaded');
  } catch (err) { toast.error('Excel download failed'); console.error(err); }
};

// ─── PDF ──────────────────────────────────────────────────────────────────────
const downloadPDF = (report, fyName, societyName) => {
  try {
    // Fix 3 — each head is its own page-breaking section
    // Fix 2 — narration + voucher/receipt no in detail rows
    // Fix 4 — orange for expense

    const buildHeadBlocks = (detailObj, sectionColor, isExpense) => {
      if (!detailObj || Object.keys(detailObj).length === 0)
        return `<div style="padding:20px;text-align:center;color:#999;">No entries</div>`;

      const refLabel = isExpense ? 'Voucher No' : 'Receipt No';

      return Object.entries(detailObj).map(([head, data], hi) => {
        const txRows = (data.transactions || []).map((t, ti) => `
          <tr style="background:${ti % 2 === 0 ? '#fafafa' : '#fff'}">
            <td style="padding:5px 10px 5px 14px;font-size:11px;color:#555;border-bottom:1px solid #f0f0f0;white-space:nowrap;">${fmtDate(t.date)}</td>
            <td style="padding:5px 10px;font-size:11px;color:#333;border-bottom:1px solid #f0f0f0;">${t.narration || '—'}</td>
            <td style="padding:5px 10px;font-size:11px;color:#444;border-bottom:1px solid #f0f0f0;">${t.memberName || '—'}</td>
            <td style="padding:5px 10px;font-size:11px;color:#666;border-bottom:1px solid #f0f0f0;font-family:monospace;">${t.receiptNo || '—'}</td>
            <td style="padding:5px 10px;font-size:12px;text-align:right;color:${sectionColor};border-bottom:1px solid #f0f0f0;font-weight:600;">₹${fmt(t.amount)}</td>
          </tr>`).join('');

        // Fix 3 — page-break-before on every head block after the first
        const pageBreak = hi > 0 ? 'page-break-before:always;' : '';

        return `
          <div style="${pageBreak}margin-bottom:0;">
            <div style="background:${isExpense ? EXPENSE_COLOR_LIGHT : '#d1fae5'};
              border:1px solid ${isExpense ? EXPENSE_COLOR_BORDER : '#a7f3d0'};
              border-radius:6px 6px 0 0;
              padding:9px 14px;
              display:flex;justify-content:space-between;align-items:center;">
              <span style="font-weight:700;font-size:13px;color:${isExpense ? EXPENSE_COLOR_TEXT : '#065f46'};">
                ${hi + 1}. ${head}
              </span>
              <span style="font-weight:800;font-size:14px;color:${sectionColor};">
                ₹${fmt(data.total)}
              </span>
            </div>
            <table style="width:100%;border-collapse:collapse;border:1px solid #e5e7eb;border-top:none;border-radius:0 0 6px 6px;overflow:hidden;">
              <thead>
                <tr style="background:#f5f5f5;">
                  <th style="padding:6px 10px 6px 14px;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.06em;color:#666;border-bottom:1px solid #ddd;text-align:left;">Date</th>
                  <th style="padding:6px 10px;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.06em;color:#666;border-bottom:1px solid #ddd;text-align:left;">Narration</th>
                  <th style="padding:6px 10px;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.06em;color:#666;border-bottom:1px solid #ddd;text-align:left;">Member / Particulars</th>
                  <th style="padding:6px 10px;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.06em;color:#666;border-bottom:1px solid #ddd;text-align:left;">${refLabel}</th>
                  <th style="padding:6px 10px;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.06em;color:#666;border-bottom:1px solid #ddd;text-align:right;">Amount (₹)</th>
                </tr>
              </thead>
              <tbody>${txRows}</tbody>
              <tfoot>
                <tr style="background:#f9fafb;border-top:2px solid #ddd;">
                  <td colspan="4" style="padding:7px 10px;font-weight:700;font-size:12px;">Sub-total — ${head}</td>
                  <td style="padding:7px 10px;text-align:right;font-weight:700;font-size:12px;color:${sectionColor};">₹${fmt(data.total)}</td>
                </tr>
              </tfoot>
            </table>
          </div>`;
      }).join('');
    };

    const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8"/>
  <title>Summary Report — ${fyName || ''}</title>
  <style>
    * { margin:0; padding:0; box-sizing:border-box; }
    body { font-family:Arial,Helvetica,sans-serif; font-size:12px; color:#1a1a2e; background:#fff; padding:24px; }
    .page-header { text-align:center; margin-bottom:24px; padding-bottom:14px; border-bottom:2px solid #F5A623; }
    .page-header h1 { font-size:18px; font-weight:900; color:#0F1A2E; margin-bottom:3px; }
    .page-header p  { font-size:11px; color:#666; margin-top:3px; }
    .section-header { padding:10px 14px; font-weight:900; font-size:13px; letter-spacing:0.08em;
      text-transform:uppercase; margin-bottom:12px; border-radius:4px; }
    .income-header  { background:#d1fae5; color:#065f46; }
    .expense-header { background:${EXPENSE_COLOR_LIGHT}; color:${EXPENSE_COLOR_TEXT}; }
    .section-total  { display:flex;justify-content:space-between;padding:10px 14px;font-weight:700;
      font-size:13px; border-radius:4px; margin-top:12px; }
    .income-total  { background:#d1fae5; color:#065f46; }
    .expense-total { background:${EXPENSE_COLOR_LIGHT}; color:${EXPENSE_COLOR_TEXT}; }
    .footer { text-align:center;font-size:10px;color:#aaa;border-top:1px solid #eee;padding-top:10px;margin-top:20px; }
    @page { margin: 0; size: auto; }
    @media print {
      body { padding:10px; }
    }
  </style>
</head>
<body>

  <div class="page-header">
    <h1>${societyName || 'Society'}</h1>
    <p>Detailed Summary Report &nbsp;|&nbsp; Financial Year: ${fyName || report.fyName}</p>

  </div>

  <!-- INCOME SECTION -->
  <div class="section-header income-header">Income</div>
  ${buildHeadBlocks(report.incomeByHeadDetail, '#059669', false)}
  <div class="section-total income-total">
    <span>Total Income</span>
    <span>₹${fmt(report.totalIncome)}</span>
  </div>

  <div style="page-break-before:always;"></div>

  <!-- EXPENSE SECTION -->
  <div class="section-header expense-header">Expense</div>
  ${buildHeadBlocks(report.expenseByHeadDetail, EXPENSE_COLOR, true)}
  <div class="section-total expense-total">
    <span>Total Expense</span>
    <span>₹${fmt(report.totalExpense)}</span>
  </div>

  <div class="footer">System-generated report from Drusshti Housing Society Management &nbsp;|&nbsp; ${fyName || report.fyName}</div>

</body>
</html>`;

    const win = window.open('', '_blank', 'width=1000,height=800');
    win.document.write(html);
    win.document.close();
    setTimeout(() => win.print(), 500);
  } catch (err) { toast.error('PDF generation failed'); console.error(err); }
};

// ─── Expandable Head Row (screen) ─────────────────────────────────────────────
const HeadSection = ({ name, data, color, isExpense }) => {
  const [open, setOpen] = useState(false);
  const txns = data?.transactions || [];

  return (
    <>
      <div
        className="summary-row"
        style={{ cursor: txns.length > 0 ? 'pointer' : 'default', userSelect: 'none' }}
        onClick={() => txns.length > 0 && setOpen(o => !o)}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
          {txns.length > 0
            ? open
              ? <ChevronDown  size={13} style={{ color: 'var(--text-dim)', flexShrink: 0 }} />
              : <ChevronRight size={13} style={{ color: 'var(--text-dim)', flexShrink: 0 }} />
            : <span style={{ display: 'inline-block', width: 13 }} />
          }
          <span style={{ fontWeight: 600, color: 'var(--text)' }}>{name}</span>
          {txns.length > 0 && (
            <span style={{ fontSize: '0.7rem', color: 'var(--text-dim)', background: 'var(--bg-overlay)', padding: '1px 7px', borderRadius: 4, fontWeight: 500 }}>
              {txns.length} {txns.length === 1 ? 'entry' : 'entries'}
            </span>
          )}
        </div>
        <span style={{ color, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>₹{fmt(data?.total || 0)}</span>
      </div>

      {/* Fix 2 — transaction rows with narration + receipt/voucher */}
      {open && txns.map((t, i) => (
        <div key={i} style={{
          display: 'grid',
          gridTemplateColumns: '90px 1fr 1fr 100px 110px',
          gap: '0 10px',
          padding: '5px 1.25rem 5px 2.2rem',
          borderBottom: '1px solid var(--border-subtle)',
          background: i % 2 === 0 ? 'var(--bg-raised)' : 'var(--bg-surface)',
          fontSize: '0.78rem',
          alignItems: 'center',
        }}>
          <span style={{ color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{fmtDate(t.date)}</span>
          <span style={{ color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.narration || '—'}</span>
          <span style={{ color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.memberName || '—'}</span>
          {/* Fix 2 — label changes by type */}
          <span style={{ color: 'var(--text-dim)', fontFamily: 'monospace', fontSize: '0.72rem' }}>
            {t.receiptNo || '—'}
          </span>
          <span style={{ color, textAlign: 'right', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>₹{fmt(t.amount)}</span>
        </div>
      ))}
    </>
  );
};

// Column headers
const DetailColHeader = ({ isExpense }) => (
  <div style={{
    display: 'grid',
    gridTemplateColumns: '90px 1fr 1fr 100px 110px',
    gap: '0 10px',
    padding: '4px 1.25rem 4px 2.2rem',
    background: 'var(--bg-overlay)',
    fontSize: '0.68rem', fontWeight: 700,
    textTransform: 'uppercase', letterSpacing: '0.06em',
    color: 'var(--text-dim)',
    borderBottom: '1px solid var(--border-subtle)',
  }}>
    <span>Date</span>
    <span>Narration</span>
    <span>Member / Particulars</span>
    {/* Fix 2 — correct label per section */}
    <span>{isExpense ? 'Voucher No' : 'Receipt No'}</span>
    <span style={{ textAlign: 'right' }}>Amount</span>
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const SummaryReport = ({ societyId, fyId, fyName, societyName }) => {
  const [report,  setReport]  = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const r = await getSummaryReport(societyId, fyId);
      setReport(r.data.summary);
    } catch { toast.error('Failed to load summary'); }
    finally { setLoading(false); }
  }, [societyId, fyId]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  if (loading) return <div className="loading-center"><div className="spinner" /></div>;
  if (!report)  return null;

  const hasDetail = report.incomeByHeadDetail || report.expenseByHeadDetail;

  return (
    <div className="animate-in">

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <div>
          <h2>Summary</h2>
          <p className="text-sm text-muted" style={{ marginTop: '0.2rem' }}>
            FY: {report.fyName || fyName}
            &nbsp;·&nbsp;
            <span style={{ color: 'var(--text-dim)' }}>Click any head row to expand transactions</span>
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <Button variant="ghost" size="sm" icon={FileSpreadsheet} onClick={() => downloadExcel(report, fyName, societyName)}>Excel</Button>
          <Button variant="ghost" size="sm" icon={FileText}        onClick={() => downloadPDF(report, fyName, societyName)}>PDF</Button>
          <Button variant="ghost" size="sm" icon={RefreshCw}       onClick={fetchReport}>Refresh</Button>
        </div>
      </div>

      <div className="summary-grid">

        {/* INCOME */}
        <div className="summary-section">
          <div className="summary-head" style={{ color: 'var(--success)' }}>Income</div>
          <DetailColHeader isExpense={false} />
          {Object.entries(
            hasDetail ? (report.incomeByHeadDetail || {}) : (report.incomeByHead || {})
          ).map(([name, data]) => (
            <HeadSection key={name} name={name}
              data={typeof data === 'object' && data.transactions ? data : { total: Number(data), transactions: [] }}
              color="var(--success)" isExpense={false} />
          ))}
          <div className="summary-row summary-total">
            <span>Total Income</span>
            <span className="positive-amount" style={{ fontVariantNumeric: 'tabular-nums' }}>₹{fmt(report.totalIncome)}</span>
          </div>
        </div>

        {/* EXPENSE — Fix 4: orange */}
        <div className="summary-section">
          <div className="summary-head" style={{ color: EXPENSE_COLOR }}>Expense</div>
          <DetailColHeader isExpense={true} />
          {Object.entries(
            hasDetail ? (report.expenseByHeadDetail || {}) : (report.expenseByHead || {})
          ).map(([name, data]) => (
            <HeadSection key={name} name={name}
              data={typeof data === 'object' && data.transactions ? data : { total: Number(data), transactions: [] }}
              color={EXPENSE_COLOR} isExpense={true} />
          ))}
          <div className="summary-row summary-total">
            <span>Total Expense</span>
            {/* Fix 4 — orange total */}
            <span style={{ color: EXPENSE_COLOR, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>₹{fmt(report.totalExpense)}</span>
          </div>
        </div>

      </div>
      {/* Fix 1 — Surplus/Deficit card removed entirely */}

    </div>
  );
};

export default SummaryReport;
